import { List } from "immutable";
import { createAction } from "redux-actions";
import * as NotiActions from "./NotificationActions";
import * as M from "../models";
import { Thunk } from "./Thunk";
import { Api } from "../services/Api";

const prefix = "CAREER_CENTER_";

export class Constants {
    public static LOADING_JOBS = prefix + "LOADING_JOBS";
    public static LOADING_RESUMES = prefix + "LOADING_RESUMES";
    public static RECEIVED_ALL_JOBS = prefix + "RECEIVED_ALL_JOBS";
    public static RECEIVED_ALL_RESUMES = prefix + "RECEIVED_ALL_RESUMES";
    public static RECEIVED_ALL_USER_JOBS = prefix + "RECEIVED_ALL_USER_JOBS";
    public static RECEIVED_ALL_USER_RESUMES = prefix + "RECEIVED_ALL_USER_RESUMES";
    public static RECEIVED_USER_SUBSCRIPTIONS = prefix + "RECEIVED_USER_SUBSCRIPTIONS";
    public static UPDATED_JOB_LIST = prefix + "UPDATED_JOB_LIST";
    public static UPDATED_RESUME_LIST = prefix + "UPDATED_RESUME_LIST";
    public static UPDATED_USER_JOB_LIST = prefix + "UPDATED_USER_JOB_LIST";
    public static UPDATED_USER_JOB_SUBSCRIPTIONS = prefix + "UPDATED_USER_JOB_SUBSCRIPTIONS";
    public static UPDATED_USER_RESUME_LIST = prefix + "UPDATED_USER_RESUME_LIST";
    public static UPDATED_USER_RESUME_SUBSCRIPTIONS = prefix + "UPDATED_USER_RESUME_SUBSCRIPTIONS";
}

export interface AllJobsReceivedPayload {
    jobs: M.CareerCenter.JobPost[];
}

export interface AllResumesReceivedPayload {
    resumes: M.CareerCenter.Resume[];
}

export type UpdateJobsPayload = List<M.CareerCenter.JobPost>;

export type UpdateResumesPayload = List<M.CareerCenter.Resume>;

export const loadJobs = (): Thunk => async (dispatch, getState) => {
    const api = new Api(getState());
    const result = await api.get<M.CareerCenter.JobPost[]>(Api.endpoints.careerCenter.jobs);

    if (result.isSuccess && result.data) {
        dispatch(onAllJobsReceived(result.data));
    }
    else {
        dispatch(NotiActions.notifyError(result.errors.join("<br />")));
    }

    dispatch(toggleJobsLoading(false));
};

export const loadResumes = (): Thunk => async (dispatch, getState) => {
    const api = new Api(getState());
    const result = await api.get<M.CareerCenter.Resume[]>(Api.endpoints.careerCenter.resumes);

    if (result.isSuccess && result.data) {
        dispatch(onAllResumesReceived(result.data));
    }
    else {
        dispatch(NotiActions.notifyError(result.errors.join("<br />")));
    }

    dispatch(toggleResumesLoading(false));
};

export const loadUserJobs = (): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);

    if (!state.auth.data || !state.auth.data.userGuid)
        return;

    const qp = { queryParams: { user: state.auth.data.userGuid }};
    const jobsPromise = api.get<M.CareerCenter.JobPost[]>(Api.endpoints.users.careerCenter.jobs, qp);
    const subsPromise = api.get<M.CareerCenter.UserSubscriptions>(Api.endpoints.users.careerCenter.subscriptions, qp);

    Promise.all([jobsPromise, subsPromise]).then((results) => {
        const jobsResult = results[0];
        const subsResult = results[1];
        const errors = jobsResult.errors.concat(subsResult.errors);

        if (jobsResult.isSuccess && jobsResult.data)
            dispatch(onAllUserJobsReceived(jobsResult.data));

        if (subsResult.isSuccess && subsResult.data)
            dispatch(onUserSubscriptionsReceived(subsResult.data));

        if (errors.length)
            throw new Error(errors.join("<br />"));

    }, (err) => {
        dispatch(NotiActions.notifyError(err));
    })
    .catch((err) => {
        dispatch(NotiActions.notifyError(err));
    })
    .then(() => {
        dispatch(toggleJobsLoading(false));
    });
};

export const loadUserResumes = (): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);

    if (!state.auth.data || !state.auth.data.userGuid)
        return;

    const qp = { queryParams: { user: state.auth.data.userGuid }};
    const resumesPromise = api.get<M.CareerCenter.Resume[]>(Api.endpoints.users.careerCenter.resumes, qp);
    const subsPromise = api.get<M.CareerCenter.UserSubscriptions>(Api.endpoints.users.careerCenter.subscriptions, qp);

    Promise.all([resumesPromise, subsPromise]).then((results) => {
        const resumesResult = results[0];
        const subsResult = results[1];
        const errors = resumesResult.errors.concat(subsResult.errors);

        if (resumesResult.isSuccess && resumesResult.data)
            dispatch(onAllUserResumesReceived(resumesResult.data));

        if (subsResult.isSuccess && subsResult.data)
            dispatch(onUserSubscriptionsReceived(subsResult.data));

        if (errors.length)
            throw new Error(errors.join("<br />"));

    }, (err) => {
        dispatch(NotiActions.notifyError(err));
    })
    .catch((err) => {
        dispatch(NotiActions.notifyError(err));
    })
    .then(() => {
        dispatch(toggleResumesLoading(false));
    });
};

export const removeFromUserJobs = (job: M.CareerCenter.JobPost): Thunk => async (dispatch, getState) => {
    const state = getState();
    const jobs = state.careerCenter.userJobs.filter((x) => x !== undefined && x.guid !== job.guid).toList();
    dispatch(updateUserJobs(jobs));
};

export const removeFromUserResumes = (resume: M.CareerCenter.Resume): Thunk => async (dispatch, getState) => {
    const state = getState();
    const resumes = state.careerCenter.userResumes.filter((x) => x !== undefined && x.guid !== resume.guid).toList();
    dispatch(updateUserResumes(resumes));
};

export const onAllJobsReceived = createAction<AllJobsReceivedPayload, M.CareerCenter.JobPost[]>(
    Constants.RECEIVED_ALL_JOBS, (jobs) => ({ jobs }),
);

export const onAllResumesReceived = createAction<AllResumesReceivedPayload, M.CareerCenter.Resume[]>(
    Constants.RECEIVED_ALL_RESUMES, (resumes) => ({ resumes }),
);

export const onAllUserJobsReceived = createAction<AllJobsReceivedPayload, M.CareerCenter.JobPost[]>(
    Constants.RECEIVED_ALL_USER_JOBS, (jobs) => ({ jobs }),
);

export const onAllUserResumesReceived = createAction<AllResumesReceivedPayload, M.CareerCenter.Resume[]>(
    Constants.RECEIVED_ALL_USER_RESUMES, (resumes) => ({ resumes }),
);

export const onUserSubscriptionsReceived = createAction<M.CareerCenter.UserSubscriptions, M.CareerCenter.UserSubscriptions>(
    Constants.RECEIVED_USER_SUBSCRIPTIONS, (subs) => (subs),
);

export const toggleJobsLoading = createAction<boolean, boolean>(
    Constants.LOADING_JOBS, (loading) => (loading),
);

export const toggleResumesLoading = createAction<boolean, boolean>(
    Constants.LOADING_RESUMES, (loading) => (loading),
);

export const updateJobs = createAction<UpdateJobsPayload, List<M.CareerCenter.JobPost>>(
    Constants.UPDATED_JOB_LIST, (jobs) => (jobs),
);

export const updateResumes = createAction<UpdateResumesPayload, List<M.CareerCenter.Resume>>(
    Constants.UPDATED_RESUME_LIST, (resumes) => (resumes),
);

export const updateUserJobs = createAction<UpdateJobsPayload, List<M.CareerCenter.JobPost>>(
    Constants.UPDATED_USER_JOB_LIST, (jobs) => (jobs),
);

export const updateUserJobSubscriptions = createAction<number[], number[]>(
    Constants.UPDATED_USER_JOB_SUBSCRIPTIONS, (categoryIds) => (categoryIds),
);

export const updateUserResumes = createAction<UpdateResumesPayload, List<M.CareerCenter.Resume>>(
    Constants.UPDATED_USER_RESUME_LIST, (resumes) => (resumes),
);

export const updateUserResumeSubscriptions = createAction<number[], number[]>(
    Constants.UPDATED_USER_RESUME_SUBSCRIPTIONS, (categoryIds) => (categoryIds),
);



// WEBPACK FOOTER //
// ./src/actions/CareerCenterActions.ts